import { create } from "zustand";
import axios, { AxiosRequestConfig } from "axios";

interface RequestState {
  getRequestNoAuth: <T>(
    url: string,
    queryParams?: Record<string, unknown>,
    config?: AxiosRequestConfig
  ) => Promise<T>;
  postRequestNoAuth: <Request, Response>(
    url: string,
    data: Request,
    config?: AxiosRequestConfig
  ) => Promise<Response>;
  getRequest: <T>(
    getAccessTokenSilently: any,
    url: string,
    queryParams?: Record<string, unknown>,
    config?: AxiosRequestConfig
  ) => Promise<T>;
  putRequest: <Request, Response>(
    getAccessTokenSilently: any,
    url: string,
    data: Request,
    queryParams?: Record<string, unknown>,
    config?: AxiosRequestConfig
  ) => Promise<Response>;
  postRequest: <Request, Response>(
    getAccessTokenSilently: any,
    url: string,
    data: Request,
    config?: AxiosRequestConfig
  ) => Promise<Response>;
  deleteRequest: <T>(
    getAccessTokenSilently: any,
    url: string,
    config?: AxiosRequestConfig
  ) => Promise<T>;
}

export const useRequestStore = create<RequestState>((set, get) => {
  const getRequestNoAuth = async <T>(
    url: string,
    queryParams?: Record<string, unknown>,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    if (process.env.REACT_APP_API_HOST && process.env.REACT_APP_API_HOST.length > 0) {
      try {
        const axiosConfig: AxiosRequestConfig = {
          ...config,
          params: queryParams
        };

        return (await axios.get<T>(`${process.env.REACT_APP_API_HOST}${url}`, axiosConfig)).data;
      } catch (error) {
        console.error("Error making GET request", error);
        throw error;
      }
    } else {
      throw new Error("API Host not found");
    }
  };
  const postRequestNoAuth = async <Request, Response>(
    url: string,
    data: Request,
    config?: AxiosRequestConfig
  ): Promise<Response> => {
    if (process.env.REACT_APP_API_HOST && process.env.REACT_APP_API_HOST.length > 0) {
      try {
        const axiosConfig: AxiosRequestConfig = {
          ...config
        };

        return (
          await axios.post<Response>(`${process.env.REACT_APP_API_HOST}${url}`, data, axiosConfig)
        ).data;
      } catch (error) {
        console.error("Error making POST request", error);
        throw error;
      }
    } else {
      throw new Error("API Host not found");
    }
  };
  const getRequest = async <T>(
    getAccessTokenSilently: any,
    url: string,
    queryParams?: Record<string, unknown>,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    if (process.env.REACT_APP_API_HOST && process.env.REACT_APP_API_HOST.length > 0) {
      try {
        let token;
        try {
          token = await getAccessTokenSilently({
            cacheMode: "cache-only"
          });
        } catch (error) {
          token = await getAccessTokenSilently();
        }
        const axiosConfig: AxiosRequestConfig = {
          ...config,
          headers: {
            ...config?.headers,
            Authorization: `Bearer ${token}`
          },
          params: queryParams
        };

        return (await axios.get<T>(`${process.env.REACT_APP_API_HOST}${url}`, axiosConfig)).data;
      } catch (error) {
        console.error("Error making GET request", error);
        throw error;
      }
    } else {
      throw new Error("API Host not found");
    }
  };

  const putRequest = async <Request, Response>(
    getAccessTokenSilently: any,
    url: string,
    data: Request,
    queryParams?: Record<string, unknown>,
    config?: AxiosRequestConfig
  ): Promise<Response> => {
    if (process.env.REACT_APP_API_HOST && process.env.REACT_APP_API_HOST.length > 0) {
      try {
        let token;
        try {
          token = await getAccessTokenSilently({
            cacheMode: "cache-only"
          });
        } catch (error) {
          token = await getAccessTokenSilently();
        }
        const axiosConfig: AxiosRequestConfig = {
          ...config,
          headers: {
            ...config?.headers,
            Authorization: `Bearer ${token}`
          },
          params: queryParams
        };

        return (
          await axios.put<Response>(`${process.env.REACT_APP_API_HOST}${url}`, data, axiosConfig)
        ).data;
      } catch (error) {
        console.error("Error making PUT request", error);
        throw error;
      }
    } else {
      throw new Error("API Host not found");
    }
  };

  const postRequest = async <Request, Response>(
    getAccessTokenSilently: any,
    url: string,
    data: Request,
    config?: AxiosRequestConfig
  ): Promise<Response> => {
    if (process.env.REACT_APP_API_HOST && process.env.REACT_APP_API_HOST.length > 0) {
      try {
        let token;
        try {
          token = await getAccessTokenSilently({
            cacheMode: "cache-only"
          });
        } catch (error) {
          token = await getAccessTokenSilently();
        }
        const axiosConfig: AxiosRequestConfig = {
          ...config,
          headers: {
            ...config?.headers,
            Authorization: `Bearer ${token}`
          }
        };

        return (
          await axios.post<Response>(`${process.env.REACT_APP_API_HOST}${url}`, data, axiosConfig)
        ).data;
      } catch (error) {
        console.error("Error making POST request", error);
        throw error;
      }
    } else {
      throw new Error("API Host not found");
    }
  };

  const deleteRequest = async <T>(
    getAccessTokenSilently: any,
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    if (process.env.REACT_APP_API_HOST && process.env.REACT_APP_API_HOST.length > 0) {
      try {
        let token;
        try {
          token = await getAccessTokenSilently({
            cacheMode: "cache-only"
          });
        } catch (error) {
          token = await getAccessTokenSilently();
        }
        const axiosConfig: AxiosRequestConfig = {
          ...config,
          headers: {
            ...config?.headers,
            Authorization: `Bearer ${token}`
          }
        };

        return (await axios.delete<T>(`${process.env.REACT_APP_API_HOST}${url}`, axiosConfig)).data;
      } catch (error) {
        console.error("Error making DELETE request", error);
        throw error;
      }
    } else {
      throw new Error("API Host not found");
    }
  };

  return {
    getRequestNoAuth,
    postRequestNoAuth,
    getRequest,
    putRequest,
    postRequest,
    deleteRequest
  };
});
